import { useEffect } from 'react';
import { Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUpdateUserMutation } from '@/features/user';
import { useAuthQuery } from '../../auth/api/auth.queries';
import { toast } from 'sonner';

const securityFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type SecurityFormData = z.infer<typeof securityFormSchema>;

export function SecurityTabContent() {
  const { userProfile } = useAuthQuery();
  const updateUserMutation = useUpdateUserMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm<SecurityFormData>({
    resolver: zodResolver(securityFormSchema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  // Trigger validation on confirmPassword when newPassword changes
  useEffect(() => {
    if (confirmPassword) {
      trigger('confirmPassword');
    }
  }, [newPassword, confirmPassword, trigger]);

  const onSubmit = async (data: SecurityFormData) => {
    if (!userProfile?.userId) {
      toast.error('User ID not found. Please re-login and try again.');
      return;
    }

    try {
      await updateUserMutation.mutateAsync({
        id: userProfile.userId,
        data: {
          password: data.newPassword,
        },
      });

      toast.success('Password updated successfully');
      reset();
    } catch (error: any) {
      console.error('[Security] Password update failed:', error);
      toast.error(error?.message || 'Failed to update password');
    }
  };

  return (
    <div className="space-y-6">
      {/* Password & Authentication Section */}
      <div className="space-y-6 border-slate-800">
        <h3 className="text-lg font-semibold text-slate-50">
          Password & Authentication
        </h3>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Current Password */}
          <div className="space-y-2">
            <Label>Current Password</Label>
            <Input
              type="password"
              placeholder="Current Password"
              showPasswordToggle
              {...register('currentPassword')}
              className={cn(
                errors.currentPassword &&
                  'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.currentPassword && (
              <p className="text-sm text-red-500">
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {/* New Password */}
            <div className="space-y-2 flex-1">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="New Password"
                showPasswordToggle
                {...register('newPassword')}
                className={cn(
                  errors.newPassword &&
                    'border-red-500 focus-visible:ring-red-500'
                )}
              />
              {errors.newPassword && (
                <p className="text-sm text-red-500">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2 flex-1">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                placeholder="Confirm Password"
                showPasswordToggle
                {...register('confirmPassword')}
                className={cn(
                  errors.confirmPassword &&
                    'border-red-500 focus-visible:ring-red-500'
                )}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Update Password Button */}
          <div className="pt-2 flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting || updateUserMutation.isPending}
              className={cn(
                (isSubmitting || updateUserMutation.isPending) &&
                  'opacity-80 cursor-not-allowed'
              )}
            >
              <Save className="w-4 h-4" />
              <span>
                {isSubmitting || updateUserMutation.isPending
                  ? 'Saving...'
                  : 'Save Changes'}
              </span>
            </Button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication Section */}
      {/* <div className="space-y-4 pt-6 border-t border-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-300 block">
              Two-Factor Authentication
            </div>
            <p className="text-xs text-slate-400">
              Add an extra layer of security
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setIsTwoFactorEnabled(!isTwoFactorEnabled)}
            className="bg-slate-800 hover:bg-slate-700"
          >
            {isTwoFactorEnabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </div> */}

      {/* Session Timeout Section */}
      {/* <div className="space-y-4 pt-6 border-t border-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-300 block">
              Session Timeout
            </div>
            <p className="text-xs text-slate-400">
              Auto-logout after inactivity
            </p>
          </div>
          <Select value={sessionTimeout} onValueChange={setSessionTimeout}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {SESSION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div> */}

      {/* Save Changes Button */}
      {/* <div className="flex justify-end pt-6 border-t border-slate-800">
        <Button type="button">
          <Save className="w-4 h-4" />
          <span>Save Changes</span>
        </Button>
      </div> */}
    </div>
  );
}
